import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { CartBundleHelper } from '@theme/cart-bundle-helper';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  static orphanCleanupDone = false;
  static orphanCleanupRunning = false;

  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #bundleHelper = new CartBundleHelper({
    getCartItemRows: () => Array.from(this.refs.cartItemRows || []),
    updateMultipleQuantities: (params) => this.#updateMultipleQuantities(params),
    changeLineKeysToQuantity: (lineKeys, quantity, action, fallbackLineNumber) =>
      this.#changeLineKeysToQuantity(lineKeys, quantity, action, fallbackLineNumber),
    handleLineError: (line, max) => this.#handleBundleQuantityError(line, max),
  });

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const bundleContext = this.#bundleHelper.getBundleContext(line);

    // Ignore child tampering
    if (bundleContext?.isBundleChild) return;

    if (quantity === 0) {
      if (bundleContext?.isBundleParent) {
        this.#bundleHelper.removeBundle(bundleContext.bundleId);
        return;
      }
      return this.onLineItemRemove(line);
    } else {
      // Cleanup orphan bundle children once per page load
      if (!CartItemsComponent.orphanCleanupDone && !CartItemsComponent.orphanCleanupRunning) {
        CartItemsComponent.orphanCleanupRunning = true;
        this.#bundleHelper.cleanupBundleChildren({ orphan: true }).finally(() => {
          CartItemsComponent.orphanCleanupRunning = false;
          CartItemsComponent.orphanCleanupDone = true;
        });
      }
    }

    if (bundleContext?.isBundleParent && bundleContext.bundleId) {
      this.#bundleHelper.syncBundleQuantities(bundleContext.bundleId, quantity);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow =
      this.refs.cartItemRows.find((row) => Number(row.dataset.lineNumber) === Number(line)) ||
      this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const bundleContext = this.#bundleHelper.getBundleContext(line);
    if (bundleContext?.bundleId) {
      this.#bundleHelper.removeBundle(bundleContext.bundleId);
      return;
    }

    const cartItemRowToRemove =
      this.refs.cartItemRows.find((row) => Number(row.dataset.lineNumber) === Number(line)) ||
      this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const { nestedRows, handled } = this.#bundleHelper.removeNestedBundleByKeys(cartItemRowToRemove, line);
    if (handled) return;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...nestedRows,
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Cart change error', {
            action: config.action,
            line,
            quantity,
            errors: parsedResponseText.errors,
          });
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Multi-line update via cart_update.
   * @param {{updates: Record<string, number>, action: string, fallbackLineNumber?: number}} params
   */
  #updateMultipleQuantities({ updates, action, fallbackLineNumber = 1 }) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();
    const { cartTotal } = this.refs;
    const sectionsToUpdate = this.#getSectionsToUpdate();
    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Cart update error', {
            action,
            updates,
            fallbackLineNumber,
            errors: parsedResponseText.errors,
          });
          const firstKey = Object.keys(updates)[0];
          const firstLine = Number(firstKey);
          const errorLine = Number.isFinite(firstLine) ? firstLine : fallbackLineNumber;
          this.#handleCartError(errorLine, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Remove/zero specific line keys sequentially via cart/change.
   * @param {string[]} lineKeys
   * @param {number} quantity
   * @param {'clear'|'change'} action
   * @param {number} fallbackLineNumber
   */
  async #changeLineKeysToQuantity(lineKeys, quantity, action, fallbackLineNumber = 1) {
    if (!lineKeys.length) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    this.#disableCartItems();
    const { cartTotal } = this.refs;
    const sectionsToUpdate = Array.from(this.#getSectionsToUpdate());

    cartTotal?.shimmer();

    /** @type {any} */
    let lastResponse;

    try {
      for (let index = 0; index < lineKeys.length; index++) {
        const includeSections = index === lineKeys.length - 1;
        /** @type {Record<string, any>} */
        const body = { id: lineKeys[index], quantity };

        if (includeSections) {
          body.sections = sectionsToUpdate.join(',');
          body.sections_url = window.location.pathname;
        }

        const responseText = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: JSON.stringify(body) })).then((response) => response.text());
        const parsedResponseText = JSON.parse(responseText);
        lastResponse = parsedResponseText;

        if (parsedResponseText.errors) {
          console.error('Cart line change error', {
            action,
            lineKey: lineKeys[index],
            quantity,
            fallbackLineNumber,
            errors: parsedResponseText.errors,
          });
          this.#handleCartError(fallbackLineNumber, parsedResponseText);
          return;
        }
      }

      resetShimmer(this);
      if (!lastResponse?.sections) return;

      const newSectionHTML = new DOMParser().parseFromString(lastResponse.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.#updateQuantitySelectors(lastResponse);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: lastResponse.sections,
        })
      );

      morphSection(this.sectionId, lastResponse.sections[this.sectionId]);
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Section ids to refresh after update.
   */
  #getSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector =
      this.refs.quantitySelectors.find((selector) => {
        const input = selector.querySelector('input');
        return input && Number(input.dataset.cartLine) === Number(line);
      }) || this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles bundle quantity errors before updating.
   * @param {number} line
   * @param {number} max
   */
  #handleBundleQuantityError(line, max) {
    const template = this.dataset.bundleQuantityError || this.dataset.quantityErrorMax || 'This item has a maximum of {{ maximum }}';
    const message = template.replace('{{ maximum }}', String(max));
    this.#handleCartError(line, { errors: message });
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#bundleHelper.cleanupBundleChildren({ outOfStock: true });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
